import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Controller, useForm } from 'react-hook-form'
import { omit } from 'lodash'
import * as api from '@/apis/aipass'
import { SettingReservationTab } from '@/components/pages/setting/reservation/tab'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { EditFooter } from '@/components/organisms/edit-footer'
import { RadioButton } from '@/components/atoms/radio-button'
import { InputField } from '@/components/molecules/input-field'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useSettingsEditGuard } from '@/hooks/use-settings-edit-guard'
import { GRAY_LABEL_HEIGHT } from '@/constants/layout'
import {
  RESERVATION_TASK_MAX_DAYS,
  RESERVATION_TASK_MIN_DAYS,
  ReservationTaskSettingParams,
} from '@/models/reservation/reservation-task-setting'
import { clampInteger } from '@/utils/number'

type PeriodRow = {
  labelKey: string
  daysKey: 'newCheckinDays' | 'updatedCheckinDays' | 'canceledCheckinDays'
  allPeriodKey: 'isNewAllPeriod' | 'isUpdatedAllPeriod' | 'isCanceledAllPeriod'
}

const PERIOD_ROWS: PeriodRow[] = [
  { labelKey: 'reservationTaskSetting.NewReservation', daysKey: 'newCheckinDays', allPeriodKey: 'isNewAllPeriod' },
  { labelKey: 'reservationTaskSetting.UpdatedReservation', daysKey: 'updatedCheckinDays', allPeriodKey: 'isUpdatedAllPeriod' },
  { labelKey: 'reservationTaskSetting.CanceledReservation', daysKey: 'canceledCheckinDays', allPeriodKey: 'isCanceledAllPeriod' },
]

const RADIO_STYLE = { radioSize: 18, labelMargin: 8 }

export const SettingReservationTaskManagement: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { guardSettingsEdit } = useSettingsEditGuard()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = useForm<ReservationTaskSettingParams>()

  const loadSetting = async () => {
    const setting = await api.fetchReservationTaskSetting()
    reset(omit(setting, 'hotelId'))
    setIsLoaded(true)
  }

  const saveSetting = async () => {
    if (!guardSettingsEdit()) return
    try {
      setIsLoading(true)
      await api.updateReservationTaskSetting(getValues())
      await loadSetting()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true)
        await loadSetting()
      } catch (error) {
        errorHandler(error)
      } finally {
        setIsLoading(false)
      }
    }
    initialize()
    // 初回表示時に1回だけ取得する。以降の再取得は保存後の loadSetting で行う
  }, [])

  const footerContent = isDirty ? <EditFooter onSave={saveSetting} onCancel={() => reset()} /> : undefined

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <TabContainer tabComponent={<SettingReservationTab currentTab="taskManagement" />} isShowFooter={isDirty}>
        {/* 取得前・取得失敗時に空のフォームを出すと、現在の設定値と区別がつかないため、取得できるまで表示しない */}
        {isLoaded && (
          <div css={mainContainerStyle}>
            <div className="block-title">{t('reservationTaskSetting.TargetPeriod')}</div>
            {PERIOD_ROWS.map(row => (
              <div className="setting-row" key={row.daysKey}>
                <div className="label-text">{t(row.labelKey)}</div>
                <div className="period-select">
                  <Controller
                    name={row.allPeriodKey}
                    control={control}
                    render={({ field }) => (
                      <>
                        <RadioButton
                          id={`${row.allPeriodKey}-all`}
                          name={row.allPeriodKey}
                          checked={field.value}
                          item={{ value: true, label: t('reservationTaskSetting.AllPeriod') }}
                          onChange={() => {
                            if (!guardSettingsEdit()) return
                            field.onChange(true)
                          }}
                          style={RADIO_STYLE}
                        />
                        <RadioButton
                          id={`${row.allPeriodKey}-days`}
                          name={row.allPeriodKey}
                          checked={!field.value}
                          item={{ value: false, label: '' }}
                          onChange={() => {
                            if (!guardSettingsEdit()) return
                            field.onChange(false)
                          }}
                          style={RADIO_STYLE}
                        />
                      </>
                    )}
                  />
                  <Trans
                    i18nKey="reservationTaskSetting.DaysBeforeCheckin"
                    components={{
                      // 文言をクリックしても日数指定のラジオを選べるよう label で関連付ける
                      l: <label className="days-text" htmlFor={`${row.allPeriodKey}-days`} />,
                      days: (
                        <Controller
                          name={row.daysKey}
                          control={control}
                          render={({ field: { onChange, value, name } }) => (
                            <InputField
                              marginBottom={0}
                              type="text"
                              value={value}
                              name={name}
                              handleChangeData={(e: React.ChangeEvent<HTMLInputElement>) => {
                                if (!guardSettingsEdit()) return
                                // レストラン設定と同じく、範囲外や空欄は入力のたびに backend の許容範囲へ丸めて入力させない
                                onChange(clampInteger(e.target.value, RESERVATION_TASK_MIN_DAYS, RESERVATION_TASK_MAX_DAYS, value))
                              }}
                              width="60px"
                              fieldWidth="60px"
                              customInputStyle={daysInputStyle}
                            />
                          )}
                        />
                      ),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </TabContainer>
    </SettingsLayout>
  )
}

const mainContainerStyle = css({
  paddingTop: 56,
  '.block-title': {
    background: '#F2F2F2',
    height: GRAY_LABEL_HEIGHT,
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 1.2,
    color: '#676767',
    alignContent: 'center',
    padding: '0 32px',
  },
  '.setting-row': {
    padding: '0 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 64,
    borderBottom: '1px solid #F2F2F2',
    '.label-text': {
      fontSize: 14,
      fontWeight: 'bold',
      letterSpacing: 1.4,
      color: '#272727',
    },
  },
  '.period-select': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    color: '#272727',
    '> div:first-of-type': { marginRight: 24 },
    '.days-text': { cursor: 'pointer' },
  },
})

const daysInputStyle = css({
  textAlign: 'center',
})
